import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Box, Flex, Text } from 'rebass/styled-components';
import { FiRotateCw } from 'react-icons/fi';
import { useMeasure } from 'react-use';
import {
  Loading, Section, AnalyticsItem, AnalyticsOverflowItem, Title,
  Button,
} from '../components';
import api from '../api';

const buttons = [{ value: '24h' }, { value: '7d' }, { value: '4w' }];

const MAX_ANALYTICS_ITEMS = 8;

const itemHeight = 80;

const AnalyticsContainer = styled(Flex)`
  min-height: ${(props) => props.itemHeight}px;
`;

const AnalyticsOverflow = styled(Box)`
  max-height: 150px;
  overflow: auto;
`;

const RotateIcon = styled(FiRotateCw)`
  stroke-width: 3px;
`;

function Analytics(props) {
  const {
    addNotification, brandId, type, title, subtitle, onlyShowTotal,
  } = props;

  const [analyticsData, setAnalyticsData] = useState({});
  const [timeInterval, setTimeInterval] = useState(buttons[0].value);
  const [isLoading, setIsLoading] = useState(true);
  const [ref, { width }] = useMeasure();
  const isMounted = useRef(true);

  function sortItems(items) {
    return items.sort((a, b) => b.count - a.count);
  }

  const getAnalyticsData = async () => {
    setIsLoading(true);
    try {
      const data = await api.mavenAnalytics.getAnalytics(brandId, timeInterval, type);

      const newData = {
        ...data,
        items: sortItems(data.items),
      };

      if (isMounted.current) {
        setAnalyticsData(newData);
      }
    } catch (error) {
      addNotification(
        error,
        'error',
      );
    }
    if (isMounted.current) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      await getAnalyticsData();
    }
    fetchData();
  }, [timeInterval]); // eslint-disable-line

  useEffect(() => () => {
    isMounted.current = false;
  }, []); // eslint-disable-line

  const getMaxAnalyticsItems = () => (width > 142 ? Math.min(
    Math.floor((width - 142) / 142),
    analyticsData.items ? analyticsData.items.length : 0,
    MAX_ANALYTICS_ITEMS,
  ) : 0);

  const getAnalyticsItems = () => {
    if (analyticsData.items) {
      return analyticsData.items.slice(0, getMaxAnalyticsItems());
    }
    return [];
  };

  const getAnalyticsOverflowItems = () => {
    if (analyticsData.items) {
      return analyticsData.items.slice(getMaxAnalyticsItems());
    }
    return [];
  };

  const hasOverFlowItems = analyticsData.items && (
    analyticsData.items.length > getMaxAnalyticsItems()
  );

  const getDescriptor = () => {
    switch (type) {
      case 'routingPolicy':
        return 'polices';
      case 'knowledgeBase':
        return 'kbs';
      case 'bot':
        return 'bots';
      case 'askMavenAPI':
        return 'requests';
      default:
        return null;
    }
  };

  return (
    <Section
      title={title}
      subtitle={subtitle}
    >
      <Flex alignItems="center">
        <Flex>
          {buttons.map((button) => (
            <Button
              key={button.value}
              smallSized
              p={1}
              mr={2}
              variant={timeInterval === button.value ? 'primary' : 'secondary'}
              onClick={() => {
                setTimeInterval(button.value);
              }}
            >
              <Text fontWeight={500} fontSize={0}>{button.value}</Text>
            </Button>
          ))}
        </Flex>
        <Button variant="tertiary" onClick={getAnalyticsData} smallSized mr={2}>
          <RotateIcon size={12} />
          <Text fontWeight={500} fontSize={0} ml={2}>Refresh</Text>
        </Button>
      </Flex>
      {!isLoading && (
        <Title mt={3} mb={0}>
          {`Most used ${getDescriptor()}`}
        </Title>
      )}
      <AnalyticsContainer flexWrap="wrap" itemHeight={itemHeight} ref={ref} data-testid={`analytics${isLoading ? '' : '-loaded'}`}>
        {isLoading && <Flex alignItems="center" justifyContent="center" flex={1}><Loading size={24} /></Flex>}
        {!isLoading
          && (typeof analyticsData.total === 'number')
          && <AnalyticsItem mode="total" data={analyticsData.total} itemHeight={itemHeight} />}
        {!isLoading
          && !onlyShowTotal
          && getAnalyticsItems().map((item) => (
            <AnalyticsItem key={item.id} data={item} itemHeight={itemHeight} />
          ))}
      </AnalyticsContainer>
      {hasOverFlowItems && <Title my={3}>{`All other ${getDescriptor()} (${analyticsData.items.length - getMaxAnalyticsItems()})`}</Title>}
      {hasOverFlowItems && (
        <AnalyticsOverflow width={['100%', '100%', '80%', '50%']}>
          {!isLoading
            && getAnalyticsOverflowItems().map((item) => (
              <AnalyticsOverflowItem key={item.id} data={item} />
            ))}
        </AnalyticsOverflow>
      )}
    </Section>
  );
}

Analytics.defaultProps = {
  onlyShowTotal: false,
  title: undefined,
  subtitle: undefined,
};

Analytics.propTypes = {
  onlyShowTotal: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  addNotification: PropTypes.func.isRequired,
  brandId: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};

export default Analytics;
